/* eslint-disable react-hooks/exhaustive-deps */
import { Table, DatePicker, Space, Button, Card, Statistic, Row, Col, message } from "antd";
import { ReloadOutlined, StopOutlined } from "@ant-design/icons";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { apiClient } from "../../../shared/api/AllApi";
import { formatCurrency } from "../../../utils/formatters";
import { filterBySearch } from "../../../shared/lib/listSearch";
import ListSearch from "../../../shared/ui/ListSearch";
import "../../../shared/styles/ListPages.css";

const { RangePicker } = DatePicker;

const CancellationsList = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.date_from = dayjs(startDate).format("YYYY-MM-DD");
      if (endDate) params.date_to = dayjs(endDate).format("YYYY-MM-DD");
      const res = await apiClient.get("/orders/cancellations", { params });
      setData(res.data || null);
    } catch (e) {
      setData(null);
      message.error(e?.response?.data?.message || t("loadError", "Failed to load"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [startDate, endDate]);

  const cancellations = useMemo(
    () =>
      filterBySearch(data?.cancellations || [], searchText, (item) => [
        item.id,
        item.customer,
        item.customer_phone,
        item.document_no,
        item.cancellation_reason,
        item.cancelled_by,
        item.refunded_amount,
        item.status,
      ]),
    [data, searchText]
  );

  const columns = [
    {
      title: <b>{t("id")}</b>,
      key: "index",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: <b>{t("cancelledAt", "Cancelled")}</b>,
      dataIndex: "cancelled_at",
      key: "cancelled_at",
      render: (v, r) => dayjs(v || r.created_at).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: <b>{t("order", "Order")}</b>,
      dataIndex: "id",
      key: "id",
      render: (id, r) => (
        <Link to={`/TableOrder/InfoOrder/${id}`}>
          {r.document_no || `#${id}`}
        </Link>
      ),
    },
    {
      title: <b>{t("customer")}</b>,
      dataIndex: "customer",
      key: "customer",
      render: (v) => v || t("noCustomer", "No customer"),
    },
    {
      title: <b>{t("totalPrice")}</b>,
      dataIndex: "total_price",
      key: "total_price",
      render: (v) => <b>{formatCurrency(v)}</b>,
    },
    {
      title: <b>{t("refunded", "Refunded")}</b>,
      dataIndex: "refunded_amount",
      key: "refunded_amount",
      render: (v) => formatCurrency(v || 0),
    },
    {
      title: <b>{t("reason", "Reason")}</b>,
      dataIndex: "cancellation_reason",
      key: "cancellation_reason",
      render: (v) => v || "—",
    },
    {
      title: <b>{t("cancelledBy", "Cancelled by")}</b>,
      dataIndex: "cancelled_by",
      key: "cancelled_by",
      render: (v) => v || "—",
    },
    {
      title: <b>{t("soldBy", "Sold by")}</b>,
      dataIndex: "sold_by",
      key: "sold_by",
      render: (v) => v || "—",
    },
  ];

  return (
    <div className="list-page">
      <Card
        title={
          <span style={{ fontWeight: 600, fontSize: "1.125rem" }}>
            <StopOutlined style={{ marginInlineEnd: 8, color: "#cf1322" }} />
            {t("cancellationsReport", "Cancellations Report")}
          </span>
        }
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
              {t("refresh", "Refresh")}
            </Button>
            <Link to="/TableOrder">
              <Button type="primary">{t("orders")}</Button>
            </Link>
          </Space>
        }
        variant="borderless"
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title={t("posCancellationsTotal", "Total Cancellations")}
                value={data?.total_cancellations ?? 0}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small">
              <div style={{ color: "rgba(0,0,0,0.45)", fontSize: 14 }}>
                {t("posCancellationsSaleAmount", "Cancelled sales amount")}
              </div>
              <div style={{ fontSize: 24, fontWeight: 600 }}>
                {formatCurrency(data?.total_sale_amount || 0)}
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small">
              <div style={{ color: "rgba(0,0,0,0.45)", fontSize: 14 }}>
                {t("posOrdersCashBack", "Total Refunded")}
              </div>
              <div style={{ fontSize: 24, fontWeight: 600 }}>
                {formatCurrency(data?.total_refund || 0)}
              </div>
            </Card>
          </Col>
        </Row>

        <Space wrap style={{ marginBottom: 16 }}>
          <ListSearch value={searchText} onChange={setSearchText} width={320} />
          <RangePicker
            onChange={(dates) => {
              setStartDate(dates?.[0] || null);
              setEndDate(dates?.[1] || null);
            }}
          />
        </Space>

        <div style={{ overflowX: "auto" }}>
          <Table
            loading={loading}
            dataSource={cancellations.map((item) => ({ ...item, key: item.id }))}
            columns={columns}
            pagination={{ pageSize: 50 }}
            scroll={{ x: "max-content" }}
            locale={{ emptyText: t("noDataFound") }}
          />
        </div>
      </Card>
    </div>
  );
};

export default CancellationsList;
